import { API_BASE_URL } from '@/config/env';

import { readRecord, readString } from './readers';
import { sanitizeForm } from './sanitize';

interface ApiClientConfig {
  getAuthToken?: () => string | null;
  getQueryDefaults?: () => Record<string, string>;
  onUnauthorized?: () => void;
}

let config: ApiClientConfig = {};

/** Sets the token getter, the default query and the 401 handler of every request. */
export function configureApiClient(next: ApiClientConfig) {
  config = next;
}

/** A failed request. Status `0` means no response. */
export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/** Returns true when the error is a 403 from the API. */
export function isForbidden(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}

/** Returns the key the table gives the status of an API error, or the fallback. */
export function refusalKey<Key extends string>(
  error: unknown,
  table: Partial<Record<number, Key>>,
  fallback: NoInfer<Key>,
): Key {
  return (error instanceof ApiError && table[error.status]) || fallback;
}

export interface RequestOptions {
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE';

/** Returns the URL of one path, with each default query parameter the path does not set. */
function urlOf(path: string): string {
  const url = new URL(`${API_BASE_URL}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(config.getQueryDefaults?.() ?? {})) {
    if (!url.searchParams.has(key)) url.searchParams.set(key, value);
  }
  return url.toString();
}

/** Returns the error of a failed response, with the backend's message when it sends one. */
async function errorOf(response: Response): Promise<ApiError> {
  const body: unknown = await response.json().catch(() => null);
  const raw = readRecord(body);
  return new ApiError(response.status, readString(raw.message) || readString(raw.detail) || response.statusText, body);
}

/** Sends one request, and throws an ApiError with status 0 when no response comes. */
async function request(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (error) {
    throw new ApiError(0, error instanceof Error ? error.message : String(error), null);
  }
}

/** Sends one request with the auth header, and throws an ApiError for every failure. */
async function send(method: Method, path: string, body?: unknown, options?: RequestOptions): Promise<Response> {
  const headers = new Headers({ Accept: 'application/json', ...options?.headers });
  const isForm = body instanceof FormData;
  if (body !== undefined && !isForm) headers.set('Content-Type', 'application/json');
  const token = config.getAuthToken?.();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const response = await request(urlOf(path), {
    method,
    headers,
    body: body === undefined || isForm ? body : JSON.stringify(body),
    signal: options?.signal,
  });
  if (response.status === 401) config.onUnauthorized?.();
  if (!response.ok) throw await errorOf(response);
  return response;
}

/** Returns the result of one read of the body, and throws an ApiError when the read fails. */
async function readBody<T>(response: Response, read: () => Promise<T>): Promise<T> {
  try {
    return await read();
  } catch (error) {
    throw new ApiError(response.status, error instanceof Error ? error.message : String(error), null);
  }
}

/** Returns the JSON body of a response, or undefined when the body is empty. */
async function json(response: Response): Promise<unknown> {
  const text = await readBody(response, () => response.text());
  return text ? readBody(response, async () => JSON.parse(text) as unknown) : undefined;
}

interface DownloadBody {
  json: unknown;
  blob: Blob;
  arrayBuffer: ArrayBuffer;
}

const READERS: { [Kind in keyof DownloadBody]: (response: Response) => Promise<DownloadBody[Kind]> } = {
  json,
  blob: (response) => response.blob(),
  arrayBuffer: (response) => response.arrayBuffer(),
};

/** Returns the body of one absolute URL as `kind`, sent with no auth header.
 *  @throws ApiError for every failure. */
async function download<Kind extends keyof DownloadBody>(
  url: string,
  kind: Kind,
  options?: RequestOptions,
): Promise<DownloadBody[Kind]> {
  const response = await request(url, { headers: options?.headers, signal: options?.signal });
  if (!response.ok) throw await errorOf(response);
  return readBody(response, () => READERS[kind](response));
}

/** Sends requests to the API and to absolute URLs, and returns each JSON body as `unknown`. */
export const apiClient = {
  get: async (path: string, options?: RequestOptions) => json(await send('GET', path, undefined, options)),
  post: async (path: string, body?: unknown, options?: RequestOptions) =>
    json(await send('POST', path, body, options)),
  put: async (path: string, body?: unknown, options?: RequestOptions) =>
    json(await send('PUT', path, body, options)),
  delete: async (path: string, options?: RequestOptions) => json(await send('DELETE', path, undefined, options)),

  upload: async (path: string, form: FormData, method: 'post' | 'put' = 'post', options?: RequestOptions) =>
    json(await send(method === 'post' ? 'POST' : 'PUT', path, await sanitizeForm(form), options)),

  download,
};
